// frontend/src/components/Products/ProductForm.jsx

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient, { productsAPI, attachmentsAPI } from '../../api/client';
import { useTranslation } from '../../context/TranslationContext';
import { useToast } from '../../hooks/useToast';
import Toast from '../Common/Toast';
import { FiSave, FiX } from 'react-icons/fi';
import FileUpload from '../Common/FileUpload';
import './ProductForm.css';

const ProductForm = () => {
  const { productId: publicId } = useParams();  // publicId from URL
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toasts, removeToast, toast_success, toast_error } = useToast();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [internalProductId, setInternalProductId] = useState(null);  // numeric PK for attachments

  const [formData, setFormData] = useState({
    productCode: '', productName: '', description: '', status: 'ACTIVE',
    category: '', vertComponentGroupCode: '', horzComponentGroupCode: '',
    baseImageAttachmentId: null, notes: ''
  });

  const [pendingFiles, setPendingFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [sizeTables, setSizeTables] = useState([]);

  useEffect(() => {
    loadSizeTables();
    if (publicId) loadProduct();
  }, [publicId]);

  const loadSizeTables = async () => {
    try {
      const res = await apiClient.get('/horz-groups');
      if (res.data.success) setSizeTables(res.data.data);
    } catch (err) { console.error('Error loading size tables:', err); }
  };

  const loadProduct = async () => {
    try {
      setLoading(true);
      const res = await productsAPI.getById(publicId);
      if (res.data.success) {
        const p = res.data.data;
        setInternalProductId(p.product_id);
        setFormData({
          productCode: p.product_code || '',
          productName: p.product_name || '',
          description: p.description || '',
          status: p.status || 'ACTIVE',
          category: p.category || '',
          vertComponentGroupCode: p.vert_component_group_code || '',
          horzComponentGroupCode: p.horz_component_group_code || '',
          baseImageAttachmentId: p.base_image_attachment_id || null,
          notes: p.notes || ''
        });
      }
    } catch (err) {
      toast_error(t('messages.error_load', 'Failed to load product'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const validateForm = () => {
    const e = {};
    if (!formData.productCode?.trim()) e.productCode = t('validation.required', 'Required');
    if (!formData.productName?.trim()) e.productName = t('validation.required', 'Required');
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) { toast_error(t('validation.fix_errors', 'Please fix validation errors')); return; }
    try {
      setSaving(true);
      const saveData = {
        productCode: formData.productCode.trim(),
        productName: formData.productName.trim(),
        description: formData.description?.trim() || null,
        status: formData.status,
        category: formData.category?.trim() || null,
        vertComponentGroupCode: formData.vertComponentGroupCode || null,
        horzComponentGroupCode: formData.horzComponentGroupCode || null,
        baseImageAttachmentId: formData.baseImageAttachmentId || null,
        notes: formData.notes?.trim() || null
      };

      let response, newPublicId, newInternalId;

      if (publicId) {
        response = await productsAPI.update(publicId, saveData);
        newPublicId = publicId;
        newInternalId = internalProductId;
      } else {
        response = await productsAPI.create(saveData);
        newPublicId = response.data.data.public_id;
        newInternalId = response.data.data.product_id;
        setInternalProductId(newInternalId);
      }

      // Upload pending files after creation (uses internal id for attachments)
      if (response.data.success && pendingFiles.length > 0) {
        try {
          await attachmentsAPI.upload('product', newInternalId, pendingFiles, '', '', formData.productCode || '');
          setPendingFiles([]);
        } catch (err) {
          toast_error(t('messages.upload_failed', 'Some files failed to upload'));
        }
      }

      toast_success(publicId
        ? t('messages.product_updated', 'Product updated successfully')
        : t('messages.product_created', 'Product created successfully')
      );
      navigate('/products');
    } catch (err) {
      toast_error(err.response?.data?.message || t('messages.save_failed', 'Failed to save product'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading">{t('loading', 'Loading...')}</div>;

  return (
    <div className="page-container">
      {/* Header */}
      <div className="sof-header">
        <div className="sof-header-title">
          <span className="sof-header-label">
            {publicId ? t('products.edit_product', 'Edit Product') : t('products.new_product', 'New Product')}
          </span>
          {publicId && formData.productCode && (
            <span className="sof-header-no">{formData.productCode}</span>
          )}
        </div>
        <div className="sof-header-actions">
          <button onClick={() => navigate('/products')} className="sof-btn sof-btn-secondary">
            <FiX size={15} /> {t('cancel', 'Cancel')}
          </button>
          <button onClick={handleSave} className="sof-btn sof-btn-primary" disabled={saving}>
            <FiSave size={15} /> {saving ? t('saving', 'Saving...') : t('save', 'Save')}
          </button>
        </div>
      </div>

      <div className="product-form-body">
        {/* Basic Information */}
        <div className="form-section">
          <h3>{t('products.basic_info', 'Basic Information')}</h3>
          <div className="form-grid-2">
            <div className="form-group">
              <label className="required">{t('products.code', 'Product Code')}</label>
              <input type="text" value={formData.productCode} maxLength={50}
                onChange={e => handleChange('productCode', e.target.value)}
                className={errors.productCode ? 'form-control-sm error' : 'form-control-sm'}
                disabled={!!publicId} />
              {errors.productCode && <span className="field-error">{errors.productCode}</span>}
            </div>

            <div className="form-group">
              <label>{t('products.status', 'Status')}</label>
              <select value={formData.status} className="form-control-sm"
                onChange={e => handleChange('status', e.target.value)}>
                <option value="ACTIVE">{t('status.active', 'Active')}</option>
                <option value="DRAFT">{t('status.draft', 'Draft')}</option>
                <option value="DISCONTINUED">{t('status.discontinued', 'Discontinued')}</option>
              </select>
            </div>

            <div className="form-group form-grid-span2">
              <label className="required">{t('products.name', 'Product Name')}</label>
              <input type="text" value={formData.productName} maxLength={200}
                onChange={e => handleChange('productName', e.target.value)}
                className={errors.productName ? 'form-control-sm error' : 'form-control-sm'} />
              {errors.productName && <span className="field-error">{errors.productName}</span>}
            </div>

            <div className="form-group form-grid-span2">
              <label>{t('products.description', 'Description')}</label>
              <textarea value={formData.description} rows={3} className="form-control-sm"
                onChange={e => handleChange('description', e.target.value)} />
            </div>

            <div className="form-group">
              <label>{t('products.category', 'Category')}</label>
              <input type="text" value={formData.category} maxLength={50}
                className="form-control-sm"
                onChange={e => handleChange('category', e.target.value)} />
            </div>

            <div className="form-group">
              <label>{t('products.size_table', 'Size Table')}</label>
              <select value={formData.horzComponentGroupCode} className="form-control-sm"
                onChange={e => handleChange('horzComponentGroupCode', e.target.value)}>
                <option value="">{t('select', 'Select...')}</option>
                {sizeTables.map(tbl => (
                  <option key={tbl.horz_group_id} value={tbl.horz_group}>
                    {tbl.horz_group}{tbl.description ? ` — ${tbl.description}` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Attachments */}
        <div className="form-section">
          <h3>{t('products.attachments', 'Attachments')}</h3>
          <FileUpload
            entityType="product"
            entityId={internalProductId}
            maxFiles={20}
            sideBySide={true}
            pendingFiles={pendingFiles}
            onPendingFilesChange={setPendingFiles}
          />
        </div>

        {/* Notes */}
        <div className="form-section">
          <h3>{t('notes', 'Notes')}</h3>
          <div className="form-group">
            <textarea value={formData.notes} rows={4} className="form-control-sm"
              placeholder={t('products.notes_placeholder', 'Additional notes or specifications...')}
              onChange={e => handleChange('notes', e.target.value)} />
          </div>
        </div>
      </div>

      <div className="toast-container">
        {toasts.map(toast => <Toast key={toast.id} {...toast} onClose={removeToast} />)}
      </div>
    </div>
  );
};

export default ProductForm;